"use client";

import React, { useCallback, useRef, useState } from "react";

import { cn } from "@/shared/helpers/cn.helper";

import { DetailsPanelFooter } from "./details-panel-footer";
import { DetailsPanelHeader } from "./details-panel-header";

interface DetailsPanelContainerProps {
  title: string;
  onClose: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  contentHeight?: "full" | "fit";
}

const MIN_WIDTH = 280;
const MAX_WIDTH = 640;
const DEFAULT_WIDTH = 352;

const scrollBodyClassName = cn(
  "min-h-0 flex-1 overflow-y-auto",
  "[scrollbar-gutter:stable]",
  "[&::-webkit-scrollbar-button]:hidden",
  "[&::-webkit-scrollbar-thumb]:bg-gray-300",
  "[&::-webkit-scrollbar-thumb]:rounded-full",
  "[&::-webkit-scrollbar-track]:mt-3",
  "[&::-webkit-scrollbar-track]:mb-3",
  "[&::-webkit-scrollbar]:w-1.5",
  "[&::-webkit-scrollbar]:bg-transparent",
  "[&::-webkit-scrollbar-track]:bg-transparent",
  "[&::-webkit-scrollbar-track]:mx-0.5",
);

export const DetailsPanelContainer: React.FC<DetailsPanelContainerProps> = ({
  title,
  onClose,
  footer,
  children,
  contentHeight = "full",
}) => {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const dragStartX = useRef<number | null>(null);
  const dragStartWidth = useRef<number>(DEFAULT_WIDTH);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragStartX.current = e.clientX;
    dragStartWidth.current = width;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (dragStartX.current === null) return;
      const delta = dragStartX.current - moveEvent.clientX;
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth.current + delta));
      setWidth(next);
    };

    const onMouseUp = () => {
      dragStartX.current = null;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [width]);

  return (
    <div
      className={cn(
        "border-light-50 relative flex min-h-0 flex-col overflow-hidden rounded-xl border bg-white",
        contentHeight === "full" ? "h-full" : "h-fit",
      )}
      style={{ width }}
    >
      <div
        className="absolute bottom-0 left-0 top-0 z-20 w-1 cursor-col-resize hover:bg-gray-300/60 active:bg-gray-400/60"
        onMouseDown={onMouseDown}
      />
      <DetailsPanelHeader
        title={title}
        onClose={onClose}
      />
      <div
        className={cn(
          contentHeight === "full" ? scrollBodyClassName : "overflow-visible",
        )}
      >
        {children}
      </div>
      {footer ? <DetailsPanelFooter>{footer}</DetailsPanelFooter> : null}
    </div>
  );
};
